import { useEffect } from 'react';

import { useAuth } from 'react-oidc-context';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';

import { useSignUpReferralStore } from '@/stores/signup-referral.store';
import { useUserStore } from '@/stores/user.store';
import { CROSS_ACCOUNT_QUERY_PARAM, getCrossAccountTargetAccountId, WORKSPACE_SETUP_PATH } from '@/utils/cross-account-setup';

function ProtectedRoute() {
  const location = useLocation();
  const { isLoading, isAuthenticated } = useAuth();
  const { isNewUser, user, org } = useUserStore();
  const [searchParams] = useSearchParams();
  // Reading `referenceSmsAccountId` from the store here (rather than once at mount) is intentional:
  // the guard must re-render when the referral store rehydrates after a FusionAuth login round-trip
  // so the cross-account decision snaps to the correct branch once the store catches up.
  const { setSignUpReference, setReferenceSmsAccountId, referenceSmsAccountId } = useSignUpReferralStore();

  //set the sign up reference and reference sms account id if user is being redirected from DP PQL Banner
  useEffect(() => {
    const smsAccountId = searchParams.get(CROSS_ACCOUNT_QUERY_PARAM);
    if (smsAccountId) setReferenceSmsAccountId(smsAccountId);
    const reference = searchParams.get('reference');
    if (reference) setSignUpReference(reference);
  }, [searchParams]);

  if (isLoading) return null;

  if (!isAuthenticated) return <Navigate to={`/login?from=${location.pathname}`} />;

  const isOrgSetupPage = location.pathname === WORKSPACE_SETUP_PATH;
  if (isOrgSetupPage) {
    const isCrossAccountCreation =
      getCrossAccountTargetAccountId({
        pathname: location.pathname,
        search: location.search,
        storedReferenceSmsAccountId: referenceSmsAccountId,
        currentOrgAccountId: org?.accountId,
      }) !== null;
    if (user && org && !isCrossAccountCreation) return <Navigate to="/dashboard" />;
    if (!isNewUser && !isCrossAccountCreation) return <Navigate to="/no-workspace-available" />;
    return <Outlet />;
  }

  if (user && !org) {
    return <Navigate to={isNewUser ? WORKSPACE_SETUP_PATH : '/no-workspace-available'} />;
  }

  return <Outlet />;
}

export default ProtectedRoute;
